import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FileDown, FileText, Loader2, LogOut, Moon, RefreshCw, Sun } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Kpis, Burnup, StudentsTable } from "@/components/partner/ReportParts";
import { Podium } from "@/components/partner/Podium";
import { QrPanel } from "@/components/partner/QrPanel";
import { RL, rdate } from "@/components/partner/reportI18n";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { api, errMsg } from "@/lib/api";

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob", timeout: 120000 });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
    a.click();
    URL.revokeObjectURL(a.href);
    return true;
  } catch (e) { toast.error(errMsg(e)); return false; }
}

function ThemeToggle({ t }) {
  const { prefs, update } = usePrefs();
  const dark = prefs.theme === "dark" || (prefs.theme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  return (
    <button data-testid="report-theme-toggle" data-theme={dark ? "dark" : "light"} onClick={() => update({ theme: dark ? "light" : "dark" })} aria-label={dark ? t.light : t.dark} title={dark ? t.light : t.dark}
      className="rounded-full border border-border p-2 transition-colors hover:bg-accent">
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function PartnerReport({ base, execBase, query = "", onLogout }) {
  const [lang, setLang] = useState(() => localStorage.getItem("yk_report_lang") || "id");
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const t = RL[lang];
  const load = () => api.get(`${base}${query}`).then((x) => { setR(x.data); setErr(null); }).catch((e) => setErr(e?.response?.status === 404 ? t.invalid : errMsg(e)));
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [base, query]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem("yk_report_lang", lang); }, [lang]);
  const sep = query ? "&" : "?";
  const inst = r?.institution;
  const exec = async () => {
    setBusy(true);
    const id = toast.loading(t.execBusy);
    await download(`${execBase}${query}${sep}lang=${lang}`, `executive-report-${inst.code}-${lang}.pdf`);
    toast.dismiss(id);
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="partner-report-page">
      <header className="border-b border-border/70 bg-card/60 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Brand to={base.startsWith("/partner/me") ? "/partner" : "#"} />
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-border bg-card p-1 text-xs" data-testid="report-lang-toggle">
              {["id", "jp"].map((l) => <button key={l} data-testid={`report-lang-${l}`} onClick={() => setLang(l)} className={`rounded-full px-3 py-1 ${lang === l ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l === "id" ? "ID" : "日本語"}</button>)}
            </div>
            <ThemeToggle t={t} />
            <button data-testid="report-refresh" onClick={load} className="rounded-full border border-border p-2 hover:bg-accent" aria-label="refresh"><RefreshCw className="h-4 w-4" /></button>
            {r && inst.report_token && <QrPanel token={inst.report_token} code={inst.code} query={query} lang={lang} />}
            {r && <button data-testid="report-exec-button" disabled={busy} onClick={exec} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}{t.exec}</button>}
            {r && <button data-testid="report-pdf-button" onClick={() => download(`${base}.pdf${query}${sep}lang=${lang}`, `laporan-${inst.code}-${lang}.pdf`)} className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background"><FileDown className="h-3.5 w-3.5" />{t.pdf}</button>}
            {onLogout && <button data-testid="partner-logout" onClick={onLogout} className="rounded-full border border-border p-2 hover:bg-accent" aria-label="logout"><LogOut className="h-4 w-4" /></button>}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6 fade-up">
        {err && <div className="soft-card p-6 text-sm text-primary" data-testid="report-error">{err}</div>}
        {!r && !err && <p className="text-sm text-muted-foreground" data-testid="report-loading">{t.loading}</p>}
        {r && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t.sub} · {inst.code}</p>
                <h1 className="mt-1 font-jpserif text-3xl sm:text-4xl" data-testid="report-institution">{inst.institution_name}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{[inst.leader_name && `${t.leader}: ${inst.leader_name}`, inst.contract_start && `${t.contract}: ${rdate(inst.contract_start, lang)} – ${rdate(inst.contract_end, lang)}`, inst.max_uses && `${t.seats}: ${inst.seats_used}/${inst.max_uses}`].filter(Boolean).join(" · ")}</p>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="report-generated">{t.updated} {r.generated_at.slice(0, 16).replace("T", " ")} JST</p>
            </div>
            <Kpis r={r} lang={lang} />
            <Burnup r={r} lang={lang} />
            <Podium r={r} lang={lang} />
            <StudentsTable r={r} lang={lang} />
          </>
        )}
      </main>
    </div>
  );
}

export default function Partner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  if (user === undefined) return null;
  if (!user) { nav("/login", { replace: true }); return null; }
  if (user.role === "student") { nav("/dashboard", { replace: true }); return null; }
  const code = user.role === "superadmin" ? params.get("code") : null;
  if (user.role === "superadmin" && !code) { nav("/admin", { replace: true }); return null; }
  return <PartnerReport base="/partner/me/report" execBase="/partner/me/executive.pdf" query={code ? `?code=${encodeURIComponent(code)}` : ""} onLogout={async () => { await logout(); nav("/"); }} />;
}

export function PublicReport() {
  const { token } = useParams();
  return <PartnerReport base={`/partner/public/${token}`} execBase={`/partner/public/${token}/executive.pdf`} />;
}
